import type { SubmitHandler } from "react-hook-form";

import { Check } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { Dispute } from "@/types/dispute";

import { useAppealDispute } from "@/api/dispute-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn, formatCurrency, formatDate } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { ReturnOrder } from "./return-order";

const timelineEvents = [
	{
		status: "Order placed",
		date: "Monday, December 11, 2024",
		completed: true
	},
	{
		status: "Shipped out",
		date: "Tuesday, December 12, 2024",
		completed: true
	},
	{
		status: "Handed over to Courier",
		date: "Tuesday, December 12, 2024",
		completed: true
	},
	{
		status: "Transit",
		date: "Tuesday, December 12, 2024",
		completed: true
	},
	{
		status: "Arrived",
		date: "Tuesday, December 12, 2024",
		completed: true
	},
	{
		status: "Handed over to Buyer",
		date: "Tuesday, December 12, 2024",
		completed: true
	}
];

function DisputeStatus({ status }: { status: string }) {
	const formattedStatus = status.replace("_", " ");

	void formattedStatus;
	const getStatusLevel = () => {
		if (status === "PENDING_REVIEW")
			return 1;
		if (status === "RESOLVED")
			return 3;
		return 2;
	};
	const statusLevel = getStatusLevel();
	return (
		<div className="relative flex justify-between">
			<div className="absolute top-3 left-4 right-4 h-0.5 bg-hover-secondary" />
			<div className="relative z-10 flex flex-col items-center gap-1">
				<div className={cn("w-6 h-6 rounded-full flex items-center justify-center bg-hover-secondary", {
					"bg-primary": statusLevel >= 1
				})}
				>
					<Check className="w-4 h-4 text-white" />
				</div>
				<span className="text-sm capitalize">Pending Review</span>
			</div>
			<div className="relative z-10 flex flex-col items-center gap-1">
				<div className={cn("w-6 h-6 rounded-full text-gray-900 flex items-center justify-center bg-hover-secondary border border-hover-secondary", {
					"bg-primary": statusLevel >= 2
				})}
				>
					{statusLevel >= 2 ? <Check className="w-4 h-4 text-white" /> : 2}
				</div>
				<span className="text-sm capitalize">Under Review</span>
			</div>
			<div className="relative z-10 flex flex-col items-center gap-1">
				{statusLevel >= 3
					? <Check className="w-4 h-4 text-white" />
					: (
							<div className={cn("w-6 h-6 rounded-full text-gray-900 flex items-center justify-center bg-hover-secondary border border-hover-secondary", {
								"bg-primary": statusLevel >= 3
							})}
							>
								3
							</div>
						)}
				<span className="text-sm">Resolved</span>
			</div>
		</div>
	);
}

const disputeSchema = z.object({
	comment: z.string().min(1, { message: "Comment is required" })
});

export default function DisputeInfo({ dispute }: { dispute: Dispute }) {
	const [isAppealDialogOpen, setIsAppealDialogOpen] = useState(false);

	const form = useForm<z.infer<typeof disputeSchema>>({
		resolver: zodResolver(disputeSchema),
		defaultValues: {
			comment: ""
		}
	});

	const appealDispute = useAppealDispute();

	const handleSubmit: SubmitHandler<z.infer<typeof disputeSchema>> = async (data) => {
		toast.promise(
			appealDispute.mutateAsync({
				disputeId: dispute.id,
				appeal: data.comment
			}).then(() => {
				setIsAppealDialogOpen(false);
			}),
			{
				loading: "Appealing dispute...",
				success: "Dispute appealed successfully",
				error: "Failed to appeal dispute"
			}
		);
	};

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-xl font-semibold mb-6">Dispute information</h2>
				<DisputeStatus status={dispute.disputeStatus} />
			</div>

			<Accordion type="single" collapsible className="w-full border-none space-y-4 ">
				<AccordionItem value="details" className="border rounded-lg bg-neutral-100">
					<AccordionTrigger className="data-[state=open]:border-b p-2">Disputed order details</AccordionTrigger>
					<AccordionContent>
						<div className="space-y-4 p-2">
							<div className="flex justify-between">
								<span className="text-gray-600">Contract ID</span>
								<span>{dispute.order.contract.contractCode}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Purchase date</span>
								<span>{formatDate(dispute.order.createdAt)}</span>
							</div>
							{/* <div className="flex justify-between">
								<span className="text-gray-600">Item in dispute</span>
								<span>Red dinner gown</span>
							</div> */}
							<div className="flex justify-between">
								<span className="text-gray-600">Buyer</span>
								<span>{dispute.order.contract.buyerId?.fullName}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Amount</span>
								<span>{formatCurrency(Number(dispute.order.orderAmount), "NGN")}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Payment method</span>
								<span className="capitalize">{dispute.order.paymentOption}</span>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>

				<AccordionItem value="status" className="border rounded-lg bg-neutral-100">
					<AccordionTrigger className="data-[state=open]:border-b p-2">Dispute status</AccordionTrigger>
					<AccordionContent>
						<div className="space-y-4 p-2">
							<div className="flex justify-between">
								<span className="text-gray-600">Status</span>
								<span>Under review</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Dispute reason</span>
								<span>{dispute.question.text}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Submission date</span>
								<span>{formatDate(dispute.createdAt)}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">Expected resolution time</span>
								<span>3 - 5 business days</span>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>

				{
					dispute.disputeStatus === "RESOLVED" && (
						<AccordionItem value="timeline" className="border rounded-lg bg-neutral-100">
							<AccordionTrigger className="data-[state=open]:border-b p-2">Return Timeline</AccordionTrigger>
							<AccordionContent>
								<div className="relative p-4">
									{timelineEvents.map((event, index) => (
										<div key={index} className="flex mb-6 last:mb-0 relative">
											{/* Line connecting timeline points */}
											{index < timelineEvents.length - 1 && (
												<div className="absolute left-3 top-6 w-0.5 bg-neutral-200 h-full" />
											)}

											{/* Timeline point */}
											<div className="mr-4 flex-shrink-0">
												<div className="rounded-full size-6 flex items-center justify-center bg-green-600 text-white">
													<Check size={18} />
												</div>
											</div>

											{/* Event content */}
											<div>
												<h3 className="text-sm font-medium text-gray-900">{event.status}</h3>
												<p className="text-xs text-gray-500">{event.date}</p>
											</div>
										</div>
									))}
								</div>

							</AccordionContent>
						</AccordionItem>
					)
				}
			</Accordion>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(handleSubmit)}>
					<Dialog open={isAppealDialogOpen} onOpenChange={setIsAppealDialogOpen}>
						<DialogTrigger asChild>
							<Button className="w-full">Appeal dispute</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Appeal dispute</DialogTitle>
							</DialogHeader>
							<DialogDescription>
								Please provide a detailed explanation of why you believe the dispute should be resolved in your favor.
							</DialogDescription>
							<FormField
								control={form.control}
								name="comment"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input placeholder="Add a comment" {...field} {...form.register("comment")} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button type="submit" disabled={appealDispute.isPending} onClick={form.handleSubmit(handleSubmit)}>
								{appealDispute.isPending ? "Appealing dispute..." : "Appeal dispute"}
							</Button>
						</DialogContent>
					</Dialog>
				</form>
			</Form>

			{
				dispute.disputeStatus === "RESOLVED" && dispute.returnFee && (
					<ReturnOrder dispute={dispute} />
				)
			}

		</div>
	);
}

// interface DetailItemProps {
// 	label: string;
// 	value: string;
// }

// interface OrderDetailsProps {
// 	from: string;
// 	destination: string;
// 	buyer: string;
// 	weight: string;
// 	status: string;
// }
// function DetailItem({ label, value }: DetailItemProps) {
// 	return (
// 		<div className="mb-4">
// 			<p className="text-gray-500 text-sm mb-1">{label}</p>
// 			<p className="font-medium">{value}</p>
// 		</div>
// 	);
// }
// function OrderDetails({ from, destination, buyer, weight, status }: OrderDetailsProps) {
// 	return (
// 		<div className="mt-6">
// 			<div className="grid grid-cols-2 gap-4">
// 				<DetailItem label="From" value={from} />
// 				<DetailItem label="Destination" value={destination} />
// 				<DetailItem label="Buyer" value={buyer} />
// 				<DetailItem label="Order weight" value={weight} />
// 			</div>

// 			<div className="mt-4 flex items-center">
// 				<p className="text-gray-500 text-sm mr-2">Status:</p>
// 				<span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
// 					{status}
// 				</span>
// 			</div>
// 		</div>
// 	);
// }
